import CloseIcon from "@mui/icons-material/Close";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	IconButton,
	InputAdornment,
	MenuItem,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { format, parseISO } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { Environment } from "../../../../shared/environment";
import { VForm, VMaskTextField } from "../../../../shared/forms";
import { useToast } from "../../../../shared/hooks";
import { AgreementRenegotiationService, INegotiationBalance } from "../../../../shared/services/api/sinister/AgreementRenegotiationService";
import { IAgreement } from "../../../../shared/services/api/sinister/SinisterAgreementService";
import { NegotiationPaymentMethod } from "../../../../shared/services/api/sinister/SinisterNegotiationService";
import { ISinisterValues, SinisterService, getSinisterValueAmount } from "../../../../shared/services/api/sinister/SinisterService";
import { globalRemoveMaskReal2, globalTranslate } from "../../../../shared/utils";
import { getNextBusinessDay } from "../../../../shared/utils/dateUtils";
import { formatDateFnsRemoveTimezone } from "../../../../shared/utils/format-date";
import { formatScaleValueBR } from "../../../../shared/utils/formatScaleValue";
import { PAYMENT_METHOD_OPTIONS, getPaymentMethodLabel } from "../../../creditRecovery/constants/paymentMethodOptions";

interface Props {
	open: boolean;
	agreement: IAgreement;
	balance?: INegotiationBalance | null;
	onClose: () => void;
	onConfirm: () => void;
}

export const ModalRenegotiateAgreement = ({ open, agreement, balance = null, onClose, onConfirm }: Props) => {
	const toastHandler = useToast();
	const [values, setValues] = useState<ISinisterValues[]>([]);
	const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
	const [paymentMethod, setPaymentMethod] = useState<NegotiationPaymentMethod>("boleto");
	const [installments, setInstallments] = useState(1);
	const [discountPercent, setDiscountPercent] = useState(0);
	const [downPaymentInput, setDownPaymentInput] = useState("0,00");
	const [downPaymentFormKey, setDownPaymentFormKey] = useState(0);
	const [downPaymentDate, setDownPaymentDate] = useState("");
	const [dueDate, setDueDate] = useState("");
	const [reason, setReason] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	useEffect(() => {
		if (!open) return;

		setPaymentMethod(agreement.paymentMethod ?? "boleto");
		setInstallments(1);
		setDiscountPercent(0);
		setDownPaymentInput("0,00");
		setDownPaymentFormKey((prev) => prev + 1);
		setDownPaymentDate(format(getNextBusinessDay(), "yyyy-MM-dd"));
		setDueDate(format(getNextBusinessDay(), "yyyy-MM-dd"));
		setReason("");
		setIsLoading(true);

		SinisterService.getAllValues(1, "", "", "", agreement.sinisterId, Environment.LIMITE_LINHAS_VALORES_BORDEROS)
			.then((result) => {
				if (result instanceof Error) return;
				const notCanceled = result.data.filter((value) => value.status !== "canceled");
				setValues(notCanceled);
				setSelectedIds(new Set(notCanceled.map((value) => value.id!).filter(Boolean)));
			})
			.finally(() => setIsLoading(false));
	}, [open, agreement.sinisterId]);

	const toggleSelected = (id: string) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	};

	const selectedValues = values.filter((value) => value.id && selectedIds.has(value.id));
	const subtotalValue = selectedValues.reduce((acc, value) => acc + getSinisterValueAmount(value), 0);
	const discountAmount = subtotalValue * (Math.min(Math.max(discountPercent, 0), 100) / 100);
	const totalValue = subtotalValue - discountAmount;
	const downPaymentValue = useMemo(() => {
		const cleaned = globalRemoveMaskReal2(String(downPaymentInput || "0"));
		const parsed = Number(cleaned);
		return Number.isFinite(parsed) ? Math.max(0, Math.min(totalValue, parsed)) : 0;
	}, [downPaymentInput, totalValue]);
	const remainingValue = Math.max(totalValue - downPaymentValue, 0);
	const installmentValue = installments > 0 ? remainingValue / installments : 0;

	const handleConfirm = async () => {
		if (selectedIds.size === 0 || !dueDate) return;

		setIsSubmitting(true);
		const result = await AgreementRenegotiationService.renegotiateBySinisterId(agreement.sinisterId, {
			newValueIds: Array.from(selectedIds),
			discountPercent: discountPercent > 0 ? discountPercent : undefined,
			paymentMethod,
			installments,
			dueDate,
			reason: reason || undefined,
			downPaymentValue: downPaymentValue > 0 ? downPaymentValue : undefined,
			downPaymentDate: downPaymentValue > 0 ? downPaymentDate : undefined,
		});
		setIsSubmitting(false);

		if (result instanceof Error) {
			toastHandler.present({ type: "error", messageOrContent: result.message, position: "top-right" });
			return;
		}

		toastHandler.present({ type: "success", messageOrContent: "Acordo renegociado com sucesso!", position: "top-right" });
		onConfirm();
	};

	return (
		<>
			<LoadingComp isopen={isLoading || isSubmitting} />
			<Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h6" color="primary" sx={{ fontWeight: 400 }}>
						Renegociar Acordo
					</Typography>
					<IconButton onClick={onClose} size="small">
						<CloseIcon fontSize="small" />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					<Stack spacing={2.5} mt={0.5}>
						<Alert severity="info" sx={{ py: 0.5 }}>
							O acordo atual ({agreement.proposal.name}) será marcado como renegociado e um novo acordo ativo será criado com as
							condições abaixo.
						</Alert>

						{balance && (
							<Box sx={{ bgcolor: "grey.50", borderRadius: 1, p: 1.5 }}>
								<Typography variant="caption" color="text.secondary" sx={{ textTransform: "uppercase" }}>
									Valores do acordo anterior
								</Typography>
								<Stack direction={{ xs: "column", sm: "row" }} spacing={2} mt={0.5}>
									<Box flex={1}>
										<Typography variant="caption" color="text.secondary">
											Pago
										</Typography>
										<Typography variant="body2" fontWeight={600}>
											{formatScaleValueBR(String(balance.paidAmount))}
										</Typography>
									</Box>
									<Box flex={1}>
										<Typography variant="caption" color="text.secondary">
											Em aberto
										</Typography>
										<Typography variant="body2" fontWeight={600}>
											{formatScaleValueBR(String(balance.openAmount))}
										</Typography>
									</Box>
									<Box flex={1}>
										<Typography variant="caption" color="text.secondary">
											Vencido
										</Typography>
										<Typography variant="body2" fontWeight={600} color={balance.overdueAmount > 0 ? "error.main" : "text.primary"}>
											{formatScaleValueBR(String(balance.overdueAmount))}
										</Typography>
									</Box>
								</Stack>
							</Box>
						)}

						<Box>
							<Typography variant="subtitle2" color="text.secondary" sx={{ textTransform: "uppercase", mb: 1 }}>
								Débitos a incluir no novo acordo
							</Typography>
							<TableContainer sx={{ maxHeight: 220 }}>
								<Table size="small" stickyHeader>
									<TableHead>
										<TableRow>
											<TableCell padding="checkbox" />
											<TableCell>Descrição</TableCell>
											<TableCell>Referência</TableCell>
											<TableCell align="center">Vencimento</TableCell>
											<TableCell align="right">Valor</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{values.map((value) => (
											<TableRow key={value.id} hover>
												<TableCell padding="checkbox">
													<Checkbox checked={!!value.id && selectedIds.has(value.id)} onChange={() => value.id && toggleSelected(value.id)} />
												</TableCell>
												<TableCell>{globalTranslate(value.typeId)}</TableCell>
												<TableCell>{value.reference}</TableCell>
												<TableCell align="center">{formatDateFnsRemoveTimezone(value.dueDate) || "-"}</TableCell>
												<TableCell align="right">{formatScaleValueBR(value.value)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>

						<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
							<TextField
								select
								fullWidth
								size="small"
								label="Forma de pagamento"
								value={paymentMethod}
								onChange={(e) => setPaymentMethod(e.target.value as NegotiationPaymentMethod)}
							>
								{PAYMENT_METHOD_OPTIONS.map((option) => (
									<MenuItem key={option.value} value={option.value}>
										{option.label}
									</MenuItem>
								))}
							</TextField>

							<TextField
								type="number"
								fullWidth
								size="small"
								label="Quantidade de parcelas"
								inputProps={{ min: 1, max: 24 }}
								value={installments}
								onChange={(e) => setInstallments(Math.max(1, Math.min(24, Number(e.target.value) || 1)))}
							/>
						</Stack>

						<Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
							<TextField
								type="number"
								fullWidth
								size="small"
								label="Desconto (%)"
								inputProps={{ min: 0, max: 100, step: 0.1 }}
								value={discountPercent}
								onChange={(e) => setDiscountPercent(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
							/>

							<TextField
								type="date"
								fullWidth
								size="small"
								label="Data do 1º pagamento"
								value={dueDate}
								onChange={(e) => setDueDate(e.target.value)}
								InputLabelProps={{ shrink: true }}
							/>
						</Stack>

						<Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
							<Box flex={1} minWidth={0}>
								<VForm
									key={downPaymentFormKey}
									onSubmit={() => {}}
									initialData={{ downPaymentValue: "0,00" }}
									onPointerEnterCapture={() => {}}
									onPointerLeaveCapture={() => {}}
									placeholder=""
								>
									<VMaskTextField
										fullWidth
										size="small"
										label="Entrada"
										name="downPaymentValue"
										mask="real"
										placeholder="0,00"
										onChange={(e: any) => setDownPaymentInput(e?.target?.value ?? "")}
										onBlur={(e: any) => setDownPaymentInput(e?.target?.value ?? "")}
										InputProps={{
											startAdornment: <InputAdornment position="start">R$</InputAdornment>,
										}}
									/>
								</VForm>
								<Typography variant="caption" color="text.secondary">
									As parcelas serão calculadas sobre o saldo restante, sem a entrada.
								</Typography>
							</Box>

							<Box flex={1} minWidth={0}>
								<TextField
									type="date"
									fullWidth
									size="small"
									label="Data da entrada"
									disabled={downPaymentValue <= 0}
									value={downPaymentDate}
									onChange={(e) => setDownPaymentDate(e.target.value)}
									InputLabelProps={{ shrink: true }}
								/>
							</Box>
						</Stack>

						<TextField
							fullWidth
							size="small"
							multiline
							minRows={2}
							label="Motivo da renegociação (opcional)"
							value={reason}
							onChange={(e) => setReason(e.target.value)}
							placeholder="Ex.: inclusão de novos débitos, cliente perdeu o prazo do acordo anterior."
						/>

						<Box>
							<Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1.5, textTransform: "uppercase" }}>
								Resumo do Novo Acordo
							</Typography>

							<Box sx={{ backgroundColor: "grey.50", borderRadius: 2, p: 2.5 }}>
								<Stack spacing={1}>
									<Box display="flex" justifyContent="space-between">
										<Typography variant="body2" color="text.secondary">
											Valor selecionado
										</Typography>
										<Typography variant="body2" fontWeight={500}>
											{formatScaleValueBR(String(subtotalValue))}
										</Typography>
									</Box>
									{discountPercent > 0 && (
										<Box display="flex" justifyContent="space-between">
											<Typography variant="body2" color="text.secondary">
												Desconto ({discountPercent}%)
											</Typography>
											<Typography variant="body2" fontWeight={500} color="error.main">
												- {formatScaleValueBR(String(discountAmount))}
											</Typography>
										</Box>
									)}
									{downPaymentValue > 0 && (
										<>
											<Box display="flex" justifyContent="space-between">
												<Typography variant="body2" color="text.secondary">
													Entrada
												</Typography>
												<Typography variant="body2" fontWeight={500} color="error.main">
													- {formatScaleValueBR(String(downPaymentValue))}
												</Typography>
											</Box>
											<Box display="flex" justifyContent="space-between">
												<Typography variant="body2" color="text.secondary">
													Saldo restante
												</Typography>
												<Typography variant="body2" fontWeight={500}>
													{formatScaleValueBR(String(remainingValue))}
												</Typography>
											</Box>
										</>
									)}
									<Box display="flex" justifyContent="space-between">
										<Typography variant="body2" color="text.secondary">
											Forma de pagamento
										</Typography>
										<Typography variant="body2" fontWeight={500}>
											{getPaymentMethodLabel(paymentMethod)}
										</Typography>
									</Box>
									<Box display="flex" justifyContent="space-between">
										<Typography variant="body2" color="text.secondary">
											Vencimento
										</Typography>
										<Typography variant="body2" fontWeight={500}>
											{dueDate ? format(parseISO(dueDate), "dd/MM/yyyy") : "-"}
										</Typography>
									</Box>
								</Stack>

								<Divider sx={{ my: 2 }} />

								<Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={1.5}>
									<Stack spacing={0.3}>
										<Typography variant="subtitle2" fontWeight={700}>
											Oferta selecionada
										</Typography>
										<Typography variant="body1" fontWeight={600}>
											{installments === 1 ? "À Vista" : `${installments}x`} de {formatScaleValueBR(String(installmentValue))}
										</Typography>
									</Stack>
								</Stack>

								<Box display="flex" justifyContent="space-between" alignItems="center">
									<Typography variant="subtitle2" fontWeight={700}>
										Valor Total do Novo Acordo
									</Typography>
									<Typography variant="subtitle1" fontWeight={700}>
										{formatScaleValueBR(String(totalValue))}
									</Typography>
								</Box>
							</Box>
						</Box>
					</Stack>
				</DialogContent>

				<DialogActions>
					<Button onClick={onClose}>Voltar</Button>
					<Button variant="contained" disabled={isSubmitting || selectedIds.size === 0 || !dueDate} onClick={handleConfirm}>
						Confirmar Renegociação
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};
